<template>
  <section
    class="signals-container"
    :class="{ dark: isDark, 'signals-collapsed': isCollapsed, 'signals-expanded': isExpanded && !isCollapsed, 'signals-sidebar': isSidebar }"
    aria-label="Indicator Insights"
  >
    <header class="signals-header">
      <div class="signals-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18h6"/>
          <path d="M10 22h4"/>
          <path d="M12 2a7 7 0 0 0-4 12.75V16h8v-1.25A7 7 0 0 0 12 2z"/>
        </svg>
        <span>Indicator Insights</span>
        <small v-if="symbolDisplay">{{ symbolDisplay }}</small>
      </div>
      <div class="signals-header-actions">
        <span class="signals-count">{{ signalRows.length }}</span>
        <button
          v-if="!isSidebar"
          class="signals-icon-btn"
          type="button"
          :title="isExpanded ? 'Restore panel size' : 'Expand panel'"
          :aria-pressed="isExpanded ? 'true' : 'false'"
          @click="toggleExpanded"
        >
          <svg v-if="isExpanded" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="4 14 10 14 10 20"/>
            <polyline points="20 10 14 10 14 4"/>
            <line x1="14" y1="10" x2="21" y2="3"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9"/>
            <polyline points="9 21 3 21 3 15"/>
            <line x1="21" y1="3" x2="14" y2="10"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        </button>
        <button
          v-if="!isSidebar"
          class="signals-icon-btn"
          type="button"
          :title="isCollapsed ? 'Show signals' : 'Collapse signals'"
          :aria-expanded="isCollapsed ? 'false' : 'true'"
          @click="toggleCollapsed"
        >
          <svg v-if="isCollapsed" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <button v-if="!isSidebar" class="signals-icon-btn" type="button" title="Close" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </header>

    <div v-show="!isCollapsed" class="signals-body">
      <div v-if="signalRows.length === 0" class="signals-empty">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18h6"/>
          <path d="M10 22h4"/>
          <path d="M12 2a7 7 0 0 0-4 12.75V16h8v-1.25A7 7 0 0 0 12 2z"/>
        </svg>
        <p class="signals-empty-title">No active insights</p>
        <p class="signals-empty-sub">VT and VT Laser insights will appear here once a timeframe has a directional setup.</p>
      </div>

      <div v-else-if="isSidebar" class="signals-compact-list" aria-label="Current timeframe insights">
        <button
          v-for="row in signalRows"
          :key="row.key"
          type="button"
          class="signals-compact-row"
          :class="row.rowClass"
          @click="$emit('select', row.item)"
        >
          <span class="signals-compact-main">
            <span class="signals-compact-tf">
              <span class="signals-pulse" aria-hidden="true"></span>
              <span>{{ row.timeframeLabel }}</span>
            </span>
            <span class="signals-compact-signal">
              <span class="signals-side">{{ row.direction }}</span>
              <span class="signals-source-wrap">
                <span v-if="row.vtDirection" class="signals-source" :class="row.vtClass">VT {{ row.vtDirection === 'LONG' ? '↑' : row.vtDirection === 'SHORT' ? '↓' : row.vtDirection }}</span>
                <span v-if="row.laserDirection" class="signals-source" :class="row.laserClass">Laser {{ row.laserDirection === 'LONG' ? '↑' : row.laserDirection === 'SHORT' ? '↓' : row.laserDirection }}</span>
              </span>
            </span>
            <span class="signals-compact-updated">
              <span>{{ row.status }}</span>
              <small>{{ row.updatedAt }}</small>
            </span>
          </span>
          <span class="signals-compact-levels">
            <small>AL <b>{{ row.entryPrice }}</b></small>
            <small>IL <b>{{ row.stoploss }}</b></small>
            <small>PZ <b>{{ row.target }}</b></small>
          </span>
        </button>
      </div>

      <div v-else class="signals-table" role="table" aria-label="Current timeframe insights">
        <div class="signals-table-head" role="row">
          <span>TF</span>
          <span>Signal</span>
          <span>AL</span>
          <span>IL</span>
          <span>PZ</span>
          <span>Updated</span>
        </div>
        <button
          v-for="row in signalRows"
          :key="row.key"
          type="button"
          class="signals-row"
          :class="row.rowClass"
          role="row"
          @click="$emit('select', row.item)"
        >
          <span class="signals-tf">
            <span class="signals-pulse" aria-hidden="true"></span>
            <span>{{ row.timeframeLabel }}</span>
          </span>
          <span class="signals-direction">
            <span class="signals-side">{{ row.direction }}</span>
            <span class="signals-source-wrap">
              <span v-if="row.vtDirection" class="signals-source" :class="row.vtClass">VT {{ row.vtDirection === 'LONG' ? '↑' : row.vtDirection === 'SHORT' ? '↓' : row.vtDirection }}</span>
              <span v-if="row.laserDirection" class="signals-source" :class="row.laserClass">Laser {{ row.laserDirection === 'LONG' ? '↑' : row.laserDirection === 'SHORT' ? '↓' : row.laserDirection }}</span>
            </span>
          </span>
          <span class="signals-price">{{ row.entryPrice }}</span>
          <span class="signals-price">{{ row.stoploss }}</span>
          <span class="signals-price">{{ row.target }}</span>
          <span class="signals-updated">
            <span class="signals-status">{{ row.status }}</span>
            <small>{{ row.updatedAt }}</small>
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
const SIGNAL_PREFIX_RE = /^[⇡⇣✓◦]\s*/;

export default {
  name: 'SignalsPanel',
  props: {
    isDark: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    currentTimeframe: { type: String, default: '' },
    symbolDisplay: { type: String, default: '' },
    variant: { type: String, default: 'drawer' },
  },
  data() {
    return {
      isCollapsed: false,
      isExpanded: false,
    };
  },
  computed: {
    isSidebar() {
      return this.variant === 'sidebar';
    },
    signalRows() {
      return (this.items || [])
        .filter(item => item && !String(item.className || '').includes('p-hidden'))
        .map(item => this.buildRow(item))
        .filter(row => row.hasSignal)
        .sort((a, b) => {
          if (a.isLatest !== b.isLatest) return a.isLatest ? -1 : 1;
          if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
          return b.updatedSort - a.updatedSort;
        });
    },
  },
  methods: {
    toggleCollapsed() {
      this.isCollapsed = !this.isCollapsed;
    },
    toggleExpanded() {
      if (this.isCollapsed) {
        this.isCollapsed = false;
      }
      this.isExpanded = !this.isExpanded;
    },
    buildRow(item) {
      const entry = item.entry || {};
      const className = String(item.className || '');
      const vtDirection = this.normalizeDirection(entry.call);
      const laserDirection = this.normalizeDirection(entry.flashSide);
      const classDirection = this.getClassDirection(className);
      const direction = classDirection || vtDirection || laserDirection;
      const isLaserPrimary = !classDirection && direction && laserDirection === direction && vtDirection !== direction;
      const sourceEntry = isLaserPrimary ? entry.flashEntry : (entry.entry || entry.flashEntry);
      const sourceStop = isLaserPrimary ? entry.flashStoploss : (entry.stoploss || entry.flashStoploss);
      const sourceTarget = isLaserPrimary ? entry.flashTarget : (entry.target || entry.flashTarget);
      const updatedValue = isLaserPrimary ? entry.latestFlashTrend : (entry.latesttrend || entry.latestFlashTrend);
      const isActive = className.includes('tc-activated') || entry.activated || entry.flashActivated;
      const isCompleted = className.includes('completed');
      const isLatest = className.includes('tc-latest-signal');
      const status = isActive ? 'Active' : isCompleted ? 'Projection zone hit' : 'Watching';

      // Convert BUY/SELL to LONG/SHORT for display
      const vtDisplayDirection = vtDirection === 'BUY' ? 'LONG' : vtDirection === 'SELL' ? 'SHORT' : vtDirection;
      const laserDisplayDirection = laserDirection === 'BUY' ? 'LONG' : laserDirection === 'SELL' ? 'SHORT' : laserDirection;

      return {
        key: item.timeframe || item.value || item.label,
        item,
        direction: direction || 'NEUTRAL',
        hasSignal: !!direction || !!vtDirection || !!laserDirection,
        vtDirection: vtDisplayDirection,
        laserDirection: laserDisplayDirection,
        vtClass: vtDisplayDirection ? `source-${vtDisplayDirection.toLowerCase()}` : '',
        laserClass: laserDisplayDirection ? `source-${laserDisplayDirection.toLowerCase()}` : '',
        timeframeLabel: this.cleanTimeframeLabel(item),
        entryPrice: this.formatPrice(sourceEntry),
        stoploss: this.formatPrice(sourceStop),
        target: this.formatPrice(sourceTarget),
        updatedAt: this.formatDate(updatedValue),
        updatedSort: this.timestampValue(updatedValue),
        status,
        isActive,
        isCompleted,
        isLatest,
        rowClass: {
          'signals-row-buy': direction === 'LONG',
          'signals-row-sell': direction === 'SHORT',
          'signals-row-active': isActive,
          'signals-row-completed': isCompleted,
          'signals-row-latest': isLatest,
          'signals-row-selected': (item.timeframe || item.value) === this.currentTimeframe,
        },
      };
    },
    cleanTimeframeLabel(item) {
      const label = String(item.label || item.timeframe || item.value || '');
      return label.replace(SIGNAL_PREFIX_RE, '').trim();
    },
    getClassDirection(className) {
      const tokens = String(className || '').split(/\s+/);
      if (tokens.includes('tc-activated-sell') || tokens.includes('tc-sell') || tokens.includes('tc-sell-completed')) {
        return 'SHORT';
      }
      if (tokens.includes('tc-activated-buy') || tokens.includes('tc-buy') || tokens.includes('tc-buy-completed')) {
        return 'LONG';
      }
      if (tokens.includes('tc-shadow-sell') || tokens.includes('tc-shadow-sell-completed')) {
        return 'SHORT';
      }
      if (tokens.includes('tc-shadow-buy') || tokens.includes('tc-shadow-buy-completed')) {
        return 'LONG';
      }
      return '';
    },
    normalizeDirection(value) {
      const direction = String(value || '').toUpperCase();
      return direction === 'LONG' || direction === 'SHORT' ? direction : '';
    },
    formatPrice(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number === 0) return '-';
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      });
    },
    timestampValue(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number <= 0) return 0;
      return number < 1000000000000 ? number * 1000 : number;
    },
    formatDate(value) {
      const ts = this.timestampValue(value);
      if (!ts) return '-';
      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) return '-';
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      if (isToday) {
        return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.signals-container {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.98);
  color: #111827;
  border: 1px solid #e0e3eb;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.signals-container.signals-collapsed {
  height: 48px !important;
}

.signals-container.signals-expanded {
  height: calc(100% - 56px) !important;
}

.signals-container.dark {
  background: rgba(18, 24, 38, 0.98);
  color: #eef1f6;
  border-color: rgba(55, 59, 75, 0.85);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.42);
}

.signals-container.signals-sidebar {
  height: 100%;
  border: none;
  border-radius: 0;
  box-shadow: none;
  backdrop-filter: none;
  background: #ffffff;
}

.signals-container.signals-sidebar.dark {
  background: rgb(18, 24, 38);
  box-shadow: none;
}

.signals-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e3eb;
  flex-shrink: 0;
  min-height: 46px;
  box-sizing: border-box;
}

.signals-container.signals-sidebar .signals-header {
  min-height: 42px;
  padding: 9px 12px;
}

.signals-container.dark .signals-header {
  border-bottom-color: rgba(55, 59, 75, 0.85);
}

.signals-title {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
}

.signals-title svg {
  color: #f97316;
  flex-shrink: 0;
}

.signals-title small {
  min-width: 0;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signals-container.dark .signals-title small {
  color: #9ca3af;
}

.signals-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.signals-count {
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  font-size: 11px;
  font-weight: 700;
}

.signals-icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.signals-icon-btn:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.signals-container.dark .signals-icon-btn {
  color: #9ca3af;
}

.signals-body {
  min-height: 0;
  max-width: 100%;
  overflow: auto;
}

.signals-container.signals-sidebar .signals-body {
  flex: 1;
}

.signals-body::-webkit-scrollbar {
  height: 5px;
  width: 5px;
}

.signals-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.signals-container.dark .signals-body::-webkit-scrollbar-thumb {
  background: #374151;
}

.signals-empty {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: #9ca3af;
}

.signals-empty-title {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  font-weight: 700;
}

.signals-container.dark .signals-empty-title {
  color: #d1d5db;
}

.signals-empty-sub {
  margin: 0;
  max-width: 360px;
  color: #9ca3af;
  font-size: 12px;
  line-height: 1.45;
}

.signals-table {
  min-width: 780px;
}

.signals-compact-list {
  display: flex;
  flex-direction: column;
}

.signals-compact-row {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 9px 12px 10px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, box-shadow 0.12s;
}

.signals-compact-row:hover {
  background: rgba(249, 115, 22, 0.07);
}

.signals-container.dark .signals-compact-row {
  border-bottom-color: rgba(55, 59, 75, 0.55);
}

.signals-container.dark .signals-compact-row:hover {
  background: rgba(249, 115, 22, 0.11);
}

.signals-compact-main {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 74px;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.signals-compact-tf {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 800;
}

.signals-compact-tf span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signals-compact-signal {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.signals-compact-updated {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
}

.signals-container.dark .signals-compact-updated {
  color: #9ca3af;
}

.signals-compact-updated small {
  color: #9ca3af;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
}

.signals-compact-levels {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 63px;
  color: #6b7280;
}

.signals-compact-levels small {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  line-height: 1.3;
  white-space: nowrap;
}

.signals-compact-levels b {
  color: #374151;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.signals-container.dark .signals-compact-levels {
  color: #9ca3af;
}

.signals-container.dark .signals-compact-levels b {
  color: #d1d5db;
}

.signals-table-head,
.signals-row {
  display: grid;
  grid-template-columns: 78px minmax(190px, 1.2fr) minmax(94px, 0.8fr) minmax(94px, 0.8fr) minmax(94px, 0.8fr) minmax(128px, 0.9fr);
  align-items: center;
  column-gap: 10px;
}

.signals-table-head {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 7px 12px;
  background: #f8fafc;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.signals-container.dark .signals-table-head {
  background: #151b2c;
  color: #9ca3af;
  border-bottom-color: rgba(55, 59, 75, 0.85);
}

.signals-row {
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, box-shadow 0.12s;
}

.signals-row:hover {
  background: rgba(249, 115, 22, 0.07);
}

.signals-container.dark .signals-row {
  border-bottom-color: rgba(55, 59, 75, 0.55);
}

.signals-container.dark .signals-row:hover {
  background: rgba(249, 115, 22, 0.11);
}

.signals-row-selected {
  box-shadow: inset 3px 0 0 #2962ff;
}

.signals-row-buy.signals-row-active {
  background: rgba(34, 197, 94, 0.08);
}

.signals-row-sell.signals-row-active {
  background: rgba(239, 68, 68, 0.08);
}

.signals-container.dark .signals-row-buy.signals-row-active {
  background: rgba(34, 197, 94, 0.12);
}

.signals-container.dark .signals-row-sell.signals-row-active {
  background: rgba(239, 68, 68, 0.12);
}

.signals-tf,
.signals-direction,
.signals-updated {
  min-width: 0;
  display: flex;
  align-items: center;
}

.signals-tf {
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
}

.signals-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
  flex-shrink: 0;
}

.signals-row-buy .signals-pulse,
.signals-row-buy .signals-side {
  background: #22c55e;
  color: #ffffff;
}

.signals-row-sell .signals-pulse,
.signals-row-sell .signals-side {
  background: #ef4444;
  color: #ffffff;
}

.signals-row-latest .signals-pulse {
  animation: signal-latest-pulse 1.8s ease-in-out infinite;
}

@keyframes signal-latest-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.25); }
}

.signals-direction {
  gap: 8px;
}

.signals-side {
  min-width: 52px;
  display: inline-flex;
  justify-content: center;
  padding: 3px 7px;
  border-radius: 999px;
  background: #9ca3af;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
}

.signals-source-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.signals-source {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}

.source-long {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}

.source-short {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.signals-container.dark .source-long {
  background: rgba(34, 197, 94, 0.18);
  color: #4ade80;
}

.signals-container.dark .source-short {
  background: rgba(239, 68, 68, 0.18);
  color: #f87171;
}

.signals-price {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #374151;
}

.signals-container.dark .signals-price {
  color: #d1d5db;
}

.signals-updated {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.signals-status {
  color: #111827;
  font-size: 11px;
  font-weight: 800;
}

.signals-container.dark .signals-status {
  color: #eef1f6;
}

.signals-updated small {
  color: #9ca3af;
  font-size: 9px;
  white-space: nowrap;
}

@media (max-width: 760px) {
  .signals-container {
    border-radius: 0;
  }

  .signals-table {
    min-width: 720px;
  }
}
</style>
